/**
 * ComplianceSettingsManager (MA-ADMIN-001 through MA-ADMIN-006)
 * 
 * Admin > Compliance section for managing global compliance settings.
 * Primary focus: TrustedForm API key management (global/tenant-level).
 * 
 * MA-ADMIN-006: Layout matches Webhook Authentication Key pattern from Calendars
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  ShieldCheck, 
  Key, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Loader2,
  RotateCcw,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

// =============================================================================
// CONNECTION STATUS HELPERS
// =============================================================================

const CONNECTION_STATUS = {
  NOT_CONFIGURED: 'not_configured',
  CONNECTED: 'connected',
  FAILED: 'failed',
  UNKNOWN: 'unknown',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ComplianceSettingsManager({ tenantId }) {
  // Global settings state
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // API key form state
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const res = await api.get('/settings/global', { params });
      setSettings(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to load compliance settings';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // =============================================================================
  // API KEY MANAGEMENT
  // =============================================================================
  
  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      toast({
        title: 'API key required',
        description: 'Please enter a valid TrustedForm API key',
        variant: 'destructive',
      });
      return;
    }
    
    setSaving(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      await api.put('/settings/global/trustedform/api-key', 
        { api_key: apiKey.trim() },
        { params }
      );
      toast({
        title: 'API key saved',
        description: 'Test the connection to verify it works.',
      });
      setApiKey('');
      setShowApiKey(false);
      await fetchSettings();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to save API key';
      toast({
        title: 'Save failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const res = await api.post('/settings/global/trustedform/test', null, { params });
      
      if (res.data?.success) {
        toast({
          title: 'Connection successful',
          description: 'TrustedForm API key is valid.',
        });
      } else {
        toast({
          title: 'Connection failed',
          description: res.data?.message || 'Please check your API key.',
          variant: 'destructive',
        });
      }
      
      await fetchSettings();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Connection test failed';
      toast({
        title: 'Test failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleRemoveApiKey = async () => {
    setDeleting(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      await api.delete('/settings/global/trustedform/api-key', { params });
      toast({
        title: 'API key removed',
      });
      setDeleteDialogOpen(false);
      await fetchSettings();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to remove API key';
      toast({
        title: 'Remove failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  // =============================================================================
  // RENDER
  // =============================================================================
  
  const trustedform = settings?.trustedform || {};
  const isConfigured = trustedform.api_key_set;
  const connectionStatus = isConfigured 
    ? (trustedform.connection_status || CONNECTION_STATUS.UNKNOWN)
    : CONNECTION_STATUS.NOT_CONFIGURED;

  // Get status badge styling - Capitalized labels
  const getStatusBadge = () => {
    if (!isConfigured) {
      return <Badge className="text-xs bg-slate-500/20 text-slate-300">Not Configured</Badge>;
    }
    switch (connectionStatus) {
      case CONNECTION_STATUS.CONNECTED:
        return <Badge className="text-xs bg-emerald-500/20 text-emerald-300">Connected</Badge>;
      case CONNECTION_STATUS.FAILED:
        return <Badge className="text-xs bg-red-500/20 text-red-300">Failed</Badge>;
      default:
        return <Badge className="text-xs bg-amber-500/20 text-amber-300">Not Tested</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-8 flex items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-indigo-400" />
          <span className="ml-2 text-slate-400">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="glass border-white/10" data-testid="compliance-error">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-red-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Failed to load compliance settings</p>
              <p className="text-xs text-red-400/80">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSettings}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="compliance-settings-manager">
      <Card className="glass border-white/10" data-testid="trustedform-settings-card">
        {/* Card Header - Matches Calendars pattern */}
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-400" />
                Compliance Settings
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Manage global compliance integrations and API keys
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {isConfigured ? (
            /* Key exists - match Webhook Authentication Key layout */
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-3">
              {/* Row 1: Label + Status Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">TrustedForm API Key</span>
                </div>
                {getStatusBadge()}
              </div>
              
              {/* Row 2: Fingerprint + Last Test */}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Fingerprint:</span>
                  <code className="text-amber-300 font-mono">
                    {trustedform.api_key_masked || '****'}
                  </code>
                </div>
                {trustedform.last_tested_at && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Last tested:</span>
                    <span className="text-slate-200">
                      {new Date(trustedform.last_tested_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Row 3: Test result message (if any) */}
              {trustedform.last_test_result && (
                <p className={`text-xs ${
                  connectionStatus === CONNECTION_STATUS.CONNECTED 
                    ? 'text-emerald-400' 
                    : connectionStatus === CONNECTION_STATUS.FAILED 
                      ? 'text-red-400'
                      : 'text-slate-500'
                }`}>
                  {trustedform.last_test_result}
                </p>
              )}

              {/* Row 4: Action buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="h-8 text-xs border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
                  data-testid="test-connection-btn"
                >
                  {testing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Test Connection
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="h-8 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
                  data-testid="remove-key-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Remove Key
                </Button>
              </div>
            </div>
          ) : (
            /* No key - show input to add */
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-300">TrustedForm API Key</span>
                </div>
                {getStatusBadge()}
              </div>
              
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your TrustedForm API key..."
                    className="bg-slate-900 border-slate-700 text-slate-100 text-sm h-9 pr-8"
                    data-testid="api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {showApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <Button
                  onClick={handleSaveApiKey}
                  disabled={saving || !apiKey.trim()}
                  size="sm"
                  className="h-9 px-3"
                  data-testid="save-api-key-btn"
                >
                  {saving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Key className="h-3 w-3 mr-1.5" />
                      Save Key
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-slate-500">
                Find your API key in{' '}
                <a
                  href="https://app.activeprospect.com/account/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-0.5"
                  data-testid="link-activeprospect"
                >
                  ActiveProspect Settings
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </p>
            </div>
          )}

          {/* How It Works - compact footer section */}
          <div className="pt-3 border-t border-white/5 space-y-2">
            <div className="flex items-center gap-1.5 text-xs text-indigo-400">
              <Info className="h-3.5 w-3.5" />
              <span className="font-medium">How It Works</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs text-slate-500">
              <div>
                <span className="text-slate-400 font-medium">1. Global Key</span>
                <p className="mt-0.5">Configure once, applies to all funnels.</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">2. Funnel Policy</span>
                <p className="mt-0.5">Each funnel: Off, Capture Only, or Enforce.</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">3. Verification</span>
                <p className="mt-0.5">Unverified leads are quarantined.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="glass border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Remove TrustedForm API Key
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 text-sm">
              Funnels with enforcement enabled will not be able to verify leads until a new key is configured.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 text-slate-300 hover:bg-white/10 border-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveApiKey}
              disabled={deleting}
              className="bg-red-500/90 text-white hover:bg-red-500"
              data-testid="confirm-remove-key-btn"
            >
              {deleting ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Trash2 className="h-3 w-3 mr-1" />
              )}
              Remove Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
